import { LinkResolver } from "@/components/creators/link-resolver";
import type { ResolvedProgram } from "@/lib/queries/projects";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { z } from "zod";

const searchShape = {
  url: z.string().optional().catch(undefined),
  task_type: z.string().optional().catch(undefined),
};
const searchSchema = z.object(searchShape);

export const Route = createFileRoute(
  "/(legacy)/creators/programs/$programId/direct",
)({
  validateSearch: searchSchema,
  component: DirectRedirect,
});

// Map JWT offerType values to internal task types
const OFFER_TYPE_TO_TASK_TYPE: Record<string, string> = {
  promo_code: "PROMO_CODES",
  link: "AFFILIATE_LINKS",
};

// Routes for each task type that can be deep-linked
const TASK_TYPE_ROUTES: Record<string, string> = {
  SIGN_BRIEF:
    "/o/$orgSlug/p/$profileSlug/c/$campaignId/sign-brief/$interactionId",
  SUBMIT_CONTENT:
    "/o/$orgSlug/p/$profileSlug/c/$campaignId/submit-content/$interactionId",
  PROMO_CODES:
    "/o/$orgSlug/p/$profileSlug/c/$campaignId/promo-codes/$interactionId",
  AFFILIATE_LINKS:
    "/o/$orgSlug/p/$profileSlug/c/$campaignId/affiliate-links/$interactionId",
  MEMBER_DATA_UPDATE:
    "/o/$orgSlug/p/$profileSlug/c/$campaignId/confirm-info/$interactionId",
  PRODUCT_SELECTION:
    "/o/$orgSlug/p/$profileSlug/c/$campaignId/product-selection/$interactionId",
};

// Convert base64url (RFC 7515) to standard base64 for atob()
export function base64UrlToBase64(b64url: string): string {
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  return b64 + "=".repeat((4 - (b64.length % 4)) % 4);
}

// Extract offerType from an offer URL containing a base64-encoded JWT
export function extractOfferType(offerUrl: string): string | null {
  try {
    const url = new URL(offerUrl);
    const pathMatch = url.pathname.match(/^\/offers\/([^/]+)/);
    if (!pathMatch) return null;

    const jwt = atob(base64UrlToBase64(pathMatch[1]));
    const payloadB64Url = jwt.split(".")[1];
    if (!payloadB64Url) return null;

    const payload = JSON.parse(atob(base64UrlToBase64(payloadB64Url))) as {
      offerType?: string;
    };
    return payload.offerType ?? null;
  } catch {
    return null;
  }
}

// Extract the offer token from an offer URL (the path segment after /offers/)
export function extractOfferToken(offerUrl: string): string | null {
  try {
    const url = new URL(offerUrl);
    const pathMatch = url.pathname.match(/^\/offers\/([^/]+)/);
    return pathMatch ? pathMatch[1] : null;
  } catch {
    return null;
  }
}

// Top-level query params that belong to this route, not the embedded URL
const TOP_LEVEL_PARAMS = new Set(Object.keys(searchShape));

// Extract the full embedded URL from the raw query string.  Everything after
// "?url=" (or "&url=") is the embedded URL — including any &token=, &embedded=
// etc. that the browser incorrectly split into separate params.
export function extractEmbeddedUrl(rawSearch: string): string | undefined {
  const match = rawSearch.match(/[?&]url=([^&]*(?:&.*)?)/);
  if (!match) return undefined;

  // Everything from the url= value to the end is the embedded URL.
  // But we need to stop at params that are truly top-level (not part of the
  // inner URL).  Since the inner URL always starts with http, we can decode
  // the first segment and reconstruct.
  const raw = match[1];

  // The part before the first unencoded & is what the browser put in "url".
  // Everything after it (token=, agreement_id=, embedded=) leaked out.
  // Reassemble: decode the url= value, then append the leaked params,
  // filtering out any top-level route params that aren't part of the inner URL.
  const safeDecode = (value: string): string | undefined => {
    try {
      return decodeURIComponent(value);
    } catch {
      return undefined;
    }
  };

  const firstAmp = raw.indexOf("&");
  if (firstAmp === -1) {
    // No leaked params
    return safeDecode(raw) ?? undefined;
  }

  const urlPart = safeDecode(raw.substring(0, firstAmp));
  // Filter out top-level params (e.g. &task_type=SIGN_BRIEF) from leaked params
  const leakedParams = raw
    .substring(firstAmp)
    .split("&")
    .filter((p) => {
      const key = p.split("=")[0];
      return key !== "" && !TOP_LEVEL_PARAMS.has(key);
    })
    .join("&");
  if (!urlPart) return undefined;
  return leakedParams ? urlPart + "&" + leakedParams : urlPart;
}

function DirectRedirect() {
  const { programId } = Route.useParams();
  const { task_type } = Route.useSearch();
  // Capture window.location.search at first render. Using useState initializer
  // (rather than a module-level constant) ensures the value is read when the
  // component actually mounts — correct whether the route is eagerly or lazily
  // loaded and regardless of which URL the SPA initially booted on.
  const [rawSearch] = useState(() =>
    typeof window !== "undefined" ? window.location.search : "",
  );
  const url = useMemo(() => extractEmbeddedUrl(rawSearch), [rawSearch]);
  const navigate = useNavigate();

  // Derive the target task type from explicit ?task_type= param, URL path, or offer payload
  const targetTaskType = useMemo(() => {
    if (task_type && TASK_TYPE_ROUTES[task_type]) return task_type;
    if (!url) return null;
    // /agreement URLs should route to SIGN_BRIEF
    try {
      const parsed = new URL(url);
      if (parsed.pathname.includes("/agreement")) return "SIGN_BRIEF";
    } catch {
      // ignore invalid URLs
    }
    const offerType = extractOfferType(url);
    if (!offerType) return null;
    return OFFER_TYPE_TO_TASK_TYPE[offerType] ?? null;
  }, [task_type, url]);

  // Extract the offer token for fallback resolution via /offers/$token
  const offerToken = useMemo(() => {
    if (!url) return null;
    return extractOfferToken(url);
  }, [url]);

  const handleNotFound = useCallback(() => {
    if (offerToken) {
      void navigate({
        to: "/offers/$token",
        params: { token: offerToken },
        replace: true,
      });
    }
  }, [navigate, offerToken]);

  const handleResolved = useCallback(
    (data: ResolvedProgram) => {
      // Try to find an interaction matching the offer's task type
      const targetInteraction = targetTaskType
        ? data.interactions.find((i) => i.taskType === targetTaskType)
        : null;

      if (targetInteraction) {
        const route = TASK_TYPE_ROUTES[targetInteraction.taskType];
        if (route) {
          void navigate({
            to: route,
            params: {
              orgSlug: data.orgSlug,
              profileSlug: data.profileSlug,
              campaignId: String(data.campaignId),
              interactionId: String(targetInteraction.id),
            },
            replace: true,
          });
          return;
        }
      }

      // Fallback: navigate to the project page showing all tasks
      void navigate({
        to: "/o/$orgSlug/p/$profileSlug/c/$campaignId",
        params: {
          orgSlug: data.orgSlug,
          profileSlug: data.profileSlug,
          campaignId: String(data.campaignId),
        },
        replace: true,
      });
    },
    [navigate, targetTaskType],
  );

  return (
    <LinkResolver
      programId={Number(programId)}
      embeddedUrl={url}
      onResolved={handleResolved}
      onNotFound={offerToken ? handleNotFound : undefined}
    />
  );
}
